/**
 * The links in our emails (2026-10-28): `PUBLIC_URL/?verify_email=<token>`,
 * `PUBLIC_URL/?reset_password=<token>` and the old address's "This wasn't
 * me" link `PUBLIC_URL/?undo_email=<token>` (2026-10-29). Each is read once
 * per page load and taken off the address bar at once, so the token
 * doesn't stay in the history or travel with a copied address.
 */
export type LinkParam = 'verify_email' | 'reset_password' | 'undo_email';

const taken = new Map<LinkParam, string | null>();

export function takeLinkParam(name: LinkParam): string | null {
  if (taken.has(name)) return taken.get(name) ?? null;
  let value: string | null = null;
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get(name)?.trim();
    if (v && /^[A-Za-z0-9_-]{16,200}$/.test(v)) value = v;
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* no window (tests) or a strange URL */
  }
  taken.set(name, value);
  return value;
}

/** Once its screen is done (so a later sign-out doesn't open it again). */
export function forgetLinkParam(name: LinkParam): void {
  taken.set(name, null);
}

/** Hands a screen a token as if it came in the address (the reset token after "This wasn't me"). */
export function offerLinkParam(name: LinkParam, value: string): void {
  taken.set(name, value);
}

// A server invite link (`PUBLIC_URL/invite/<code>`, or `/?invite=<code>`)
// is read by shell/InviteLink.tsx `takeInviteLink()`, signed in or out.

/** Tests only. */
export function resetLinkParams(): void {
  taken.clear();
}
